import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AnnouncementBanner from "../components/AnnouncementBanner";
import DropNotificationsModal from "../components/DropNotificationsModal";
import ProductCard from "../components/ProductCard";
import {
  createWatch,
  deleteWatch,
  getHealth,
  getProducts,
  getStats,
  getWatches,
  isUnauthorized,
  toMessage,
  useApi,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { relativeTime } from "../lib/format";
import { useSocketEvent } from "../lib/socket";
import type { Product, Watch } from "../lib/types";

/**
 * The listing: every watched product once, in a grid.
 *
 * An earlier version grouped this by the URL each product was rendered on,
 * which mirrored the storefront but read badly — the same garment appeared
 * several times because several pages happen to render it, and the page was
 * more prominent than the thing being watched. Page structure is still
 * recorded and still visible on the product and site-map screens, where it
 * describes what it actually is: a fact about the storefront, not the product.
 */

/**
 * The server returns live pieces first, newest drop first, then sold-out pieces
 * newest first. This keeps that split true as sockets flip pieces in and out of
 * stock mid-visit, without reshuffling anything inside either group.
 */
function order(products: Product[]): Product[] {
  return [...products.filter((p) => p.available), ...products.filter((p) => !p.available)];
}

/** The headline counts, and the way in. Everything above the grid lives here. */
function Masthead() {
  const { isAdmin } = useAuth();
  const { data: stats } = useApi((signal) => getStats(signal), []);
  const { data: health } = useApi((signal) => getHealth(signal), []);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const polled = health?.lastRun?.startedAt;

  return (
    <>
      {/*
        The page's one <h1>, describing what this page actually is.
        
        It is visually hidden because the design deliberately has no headline —
        but a page with no h1 gives a search engine nothing to read its subject
        from, and a screen reader nothing to announce. This is not cloaking: it
        states plainly what the page below it does, which is the test that
        matters. Make it visible any time by dropping the class.
      */}
      <h1 className="ap-visually-hidden">
        ANTI PROMO drop notifications and restock alerts — live drop monitor
      </h1>

      <AnnouncementBanner />
      <header className="ap-masthead">
      <div className="ap-masthead__counts">
        <div className="ap-stat">
          <span className="ap-stat__n">{stats?.products ?? "—"}</span>
          <span className="ap-stat__k">drops tracked</span>
        </div>
        <div className="ap-stat">
          <span className="ap-stat__n">{stats?.live ?? "—"}</span>
          <span className="ap-stat__k">in stock</span>
        </div>
        <div className="ap-stat">
          <span className="ap-stat__n">{stats?.soldOut ?? "—"}</span>
          <span className="ap-stat__k">sold out</span>
        </div>
        {/* Activity volume says how closely the site is being watched, which is
            operator information rather than a headline for visitors. The API
            withholds it too, so this is not merely hidden. */}
        {isAdmin && (
          <div className="ap-stat">
            <span className="ap-stat__n">{stats?.events24h ?? "—"}</span>
            <span className="ap-stat__k">moves today</span>
          </div>
        )}
      </div>

      <div className="ap-masthead__cta">
        {/* Opens the aftrdrop invitation: how to apply, and the iOS beta. */}
        <button
          type="button"
          className="ap-btn ap-btn--cta"
          onClick={() => setNotificationsOpen(true)}
        >
          Get drop notifications
        </button>
        {polled && <p className="ap-muted">last checked {relativeTime(polled)}</p>}
        </div>
      </header>

      <DropNotificationsModal
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
    </>
  );
}

export default function Shop() {
  const { data, loading, error, reload } = useApi((signal) => getProducts(undefined, signal), []);
  const { authenticated } = useAuth();

  const [watches, setWatches] = useState<Watch[]>([]);
  const [watchError, setWatchError] = useState<string | null>(null);
  const [tokenRejected, setTokenRejected] = useState(false);
  // One request per product at a time, so a double click cannot race itself.
  const inFlight = useRef<Set<number>>(new Set());

  // Live state starts from the fetch and is then patched by sockets, so the
  // page keeps up with a drop without anyone refreshing.
  const [live, setLive] = useState<Product[] | null>(null);
  useEffect(() => setLive(data ?? null), [data]);

  const products = useMemo(() => order(live ?? []), [live]);
  const canWatch = authenticated && !tokenRejected;

  useSocketEvent<{
    productId: number;
    available: boolean;
    availableSizes: string[];
    priceCents: number;
  }>("ap:product-updated", (update) => {
    setLive((prev) =>
      prev
        ? prev.map((p) =>
            p.productId === update.productId
              ? {
                  ...p,
                  available: update.available,
                  availableSizes: update.availableSizes,
                  priceCents: update.priceCents,
                  availableVariants: update.availableSizes.length,
                  status: update.available ? "live" : "sold-out",
                }
              : p
          )
        : prev
    );
  });

  // A piece the page has never seen cannot be patched in from an event payload,
  // so a genuinely new arrival triggers a refetch. Restocks and sellouts of
  // known pieces are handled above without one.
  useSocketEvent<{ type: string; productId?: number }>("ap:event", (event) => {
    if (event.type !== "product-new" && event.type !== "product-returned") return;
    reload();
  });

  useEffect(() => {
    if (!authenticated) {
      setWatches([]);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    getWatches(controller.signal)
      .then((list) => {
        if (active) setWatches(list ?? []);
      })
      .catch((err: unknown) => {
        if (!active || controller.signal.aborted) return;
        // A rejected token means read-only, which is a state, not an error.
        if (isUnauthorized(err)) setTokenRejected(true);
        else setWatchError(toMessage(err));
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [authenticated]);

  const watchByProduct = useMemo(() => {
    const map = new Map<number, Watch>();
    for (const watch of watches) {
      if (watch.active !== false) map.set(watch.productId, watch);
    }
    return map;
  }, [watches]);

  const run = useCallback(async (productId: number, action: () => Promise<void>) => {
    if (inFlight.current.has(productId)) return;
    inFlight.current.add(productId);
    setWatchError(null);
    try {
      await action();
    } catch (err) {
      if (isUnauthorized(err)) setTokenRejected(true);
      else setWatchError(toMessage(err));
    } finally {
      inFlight.current.delete(productId);
    }
  }, []);

  const toggleWatch = useCallback(
    (product: Product) => {
      const existing = watchByProduct.get(product.productId);
      void run(product.productId, async () => {
        if (existing) {
          await deleteWatch(existing._id);
          setWatches((prev) => prev.filter((w) => w._id !== existing._id));
          return;
        }
        const created = await createWatch({ productId: product.productId, sizes: [] });
        setWatches((prev) => [...prev.filter((w) => w.productId !== created.productId), created]);
      });
    },
    [run, watchByProduct]
  );

  const toggleSize = useCallback(
    (product: Product, size: string) => {
      const existing = watchByProduct.get(product.productId);
      const current = existing?.sizes ?? [];
      const sizes = current.includes(size)
        ? current.filter((s) => s !== size)
        : [...current, size];

      void run(product.productId, async () => {
        const saved = await createWatch({ productId: product.productId, sizes });
        setWatches((prev) => [...prev.filter((w) => w.productId !== saved.productId), saved]);
      });
    },
    [run, watchByProduct]
  );

  if (loading) {
    return (
      <div className="ap-page">
        <Masthead />
        <div className="ap-state">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ap-page">
        <Masthead />
        <div className="ap-state ap-state--error">{toMessage(error)}</div>
        <button type="button" className="ap-btn" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="ap-page">
      <Masthead />

      {watchError && <div className="ap-state ap-state--error">{watchError}</div>}

      {products.length === 0 ? (
        <div className="ap-state">Nothing has been seen yet. The first poll will fill this in.</div>
      ) : (
        <div className="ap-grid">
          {products.map((product, i) => {
            const watch = watchByProduct.get(product.productId);
            return (
              <ProductCard
                key={product.productId}
                product={product}
                priority={i < 2}
                watched={Boolean(watch)}
                watchedSizes={watch?.sizes}
                onToggleWatch={canWatch ? () => toggleWatch(product) : undefined}
                onToggleSize={canWatch ? (size) => toggleSize(product, size) : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
